import { useEffect, useRef } from "react"
import { logger, LogContext } from "../../helpers/logger"

// plays the streamer's live hls feed muted, as the backdrop for the overlay preview.
// twitch's own player won't autoplay (and pauses) while anything sits on top of it, a plain video element doesn't care
export const StreamVideo = ({ src, className, onError }: { src: string, className?: string, onError: (reason: string) => void }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let isCancelled = false
    let destroyHls: (() => void) | undefined

    const fail = (reason: string, details?: Record<string, string | number | boolean | undefined>) => {
      if (isCancelled) return
      logger.warn(LogContext.OVERLAY, 'Builder stream preview failed', { reason, ...details })
      onErrorRef.current(reason)
    }

    // react sets muted as a property after mount, set it before anything loads so chrome's muted autoplay rule applies
    video.muted = true
    const play = () => {
      video.play().catch((error: unknown) => {
        // a newer load or the unmount cut the play request short, that isn't the stream failing
        if (error instanceof DOMException && error.name === 'AbortError') return
        fail('play-rejected', { error: String(error) })
      })
    }

    const handleVideoError = () => fail('video-error', { code: video.error?.code, message: video.error?.message })
    video.addEventListener('error', handleVideoError)

    const playNatively = () => {
      video.src = src
      play()
    }

    // hls.js wherever media source extensions exist, native hls only as the fallback (ios safari has no mse).
    // chrome 15x answers "maybe" to canPlayType for hls but can't play twitch's playlists, so checking that first broke it.
    // hls.js is loaded on demand so the overlay bundle never ships it
    import('hls.js').then(({ default: Hls }) => {
      if (isCancelled) return
      if (!Hls.isSupported()) {
        if (video.canPlayType('application/vnd.apple.mpegurl')) playNatively()
        else fail('hls-unsupported')
        return
      }
      // stay close to the live edge, it's a preview not a viewer
      const hls = new Hls({ liveSyncDurationCount: 2 })
      hls.on(Hls.Events.MANIFEST_PARSED, play)
      // an expired signed url or an ended stream, either way there's nothing to show
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return
        fail('hls-fatal', { type: data.type, details: data.details, status: data.response?.code })
      })
      hls.loadSource(src)
      hls.attachMedia(video)
      destroyHls = () => hls.destroy()
    }).catch((error: unknown) => fail('hls-load-failed', { error: String(error) }))

    return () => {
      isCancelled = true
      video.removeEventListener('error', handleVideoError)
      destroyHls?.()
      video.removeAttribute('src')
      video.load()
    }
  }, [src])

  return <video ref={videoRef} className={className} muted autoPlay playsInline disablePictureInPicture />
}
